<template>
  <div>
    <h5>Sincronizar códigos de ingressos</h5>

    <div
      v-if="!isCompatible"
      class="d-flex flex-column align-center text-center my-12"
    >
      <v-icon size="64" color="error">mdi-alert-circle</v-icon>
      <h5 class="mt-2">Navegador Incompatível</h5>
      <p class="mb-0">
        O navegador não é compatível com a sincronização de arquivos. Utilize na
        navegadores como Edge ou Chrome.
      </p>
    </div>

    <template v-else>
      <v-alert type="info" dense text>
        <b>Entenda o processo</b>
        <p class="mb-0">
          O processo sincroniza os códigos de ingressos de um evento especifico.
        </p>
      </v-alert>

      <!-- HERE -->
      <party-selector v-model="party" :disabled="loading" />
      <v-alert
        v-if="!isEventInNext3Days && !!party"
        type="warning"
        text
        class="mt-4"
      >
        O evento deve ser nos próximos 3 dias para sincronizar os códigos de
        ingressos.
      </v-alert>
      <template v-else-if="!!party">
        <v-select
          v-model="exportFormat"
          :items="exportFormatsOptions"
          :disabled="loading"
          outlined
          dense
          label="Formato de exportação"
          item-text="label"
          item-value="value"
          class="mt-4"
          hide-details
        />

        <v-card
          class="d-flex align-center gap-2 my-4 pa-3"
          outlined
          :disabled="loading"
        >
          <v-icon
            >mdi-{{ exportFormat === "xlsx" ? "file-excel" : "file" }}</v-icon
          >
          <p class="mb-0">
            {{ fileHandler?.name }}
          </p>
          <v-spacer />
          <v-btn v-if="!!fileHandler" @click="selectFile" text small>
            Alterar
            <v-icon small right>mdi-pencil</v-icon>
          </v-btn>
          <v-btn v-else @click="selectFile" text small>
            Selecionar
            <v-icon small right
              >mdi-{{ exportFormat === "xlsx" ? "file-excel" : "file" }}</v-icon
            >
          </v-btn>
        </v-card>
        <v-select
          v-if="!!fileHandler"
          v-model="interval"
          :items="intervalsOptions"
          :disabled="loading"
          outlined
          dense
          item-text="label"
          item-value="value"
          label="Intervalo de atualização"
        />
      </template>

      <v-alert v-if="error" type="error">
        {{ error }}
      </v-alert>
      <template v-else-if="loading">
        <v-progress-linear
          :indeterminate="timeToSync <= 0"
          :value="(timeToSync / interval) * 100"
          color="primary"
          height="22"
          rounded
        >
          <span v-if="timeToSync > 0">
            Próxima atualização em
            {{ mathRound(timeToSync / 1000) }} segundos
          </span>
          <span v-else>Sincronizando dados...</span>
        </v-progress-linear>
        <p class="text-caption text-center mt-1">
          Os ingressos estão sendo processados localmente, não feche a página.
        </p>
      </template>
      <v-alert v-else-if="succeeded" type="success">
        <b class="d-block"> Processo finalizado com sucesso </b>
        Foram processados {{ processedTickets.size | number }} ingressos
      </v-alert>
    </template>

    <div class="d-flex justify-space-between mt-4">
      <v-btn v-if="loading" @click="stop" text>
        <v-icon small left>mdi-pause-octagon</v-icon>
        Parar
      </v-btn>
      <v-btn v-else @click="$emit('close')" :disabled="loading" text>
        Fechar
      </v-btn>

      <v-btn
        v-if="isCompatible"
        @click="exportProcess"
        :loading="loading"
        :disabled="!isEventInNext3Days || !party || !fileHandler"
        color="primary"
      >
        Exportar
      </v-btn>
    </div>
  </div>
</template>

<script>
import FINANCIAL from "@/services/admin/financial";
import moment from "moment";
import ExcelJS from "exceljs";
import PartySelector from "@/components/global/selector/PartySelector.vue";

export const manifest = {
  name: "Sincronizar códigos de ingressos",
  description: "Sincroniza os códigos de ingressos de um evento específico.",
  icon: "mdi-ticket",
  group: "party",
  formats: ["xlsx", "csv"],
};

export default {
  name: "SyncTicketCodes",
  components: {
    PartySelector,
  },
  data: () => ({
    isCompatible: false,
    loading: false,
    error: false,
    party: null,
    succeeded: false,

    totalTickets: 0,
    processedTickets: new Map(),
    exportTime: 0,

    workbook: null,
    worksheet: null,
    fileHandler: null,
    exportFormat: "xlsx",
    exportFormatsOptions: [
      {
        label: "Excel",
        value: "xlsx",
      },
      {
        label: "CSV",
        value: "csv",
      },
    ],

    // sync
    lastSync: null,
    interval: 60000,
    timeToSync: 0,
    timeout: null,
    intervalsOptions: [
      {
        label: "20 segundos",
        value: 20000,
      },
      {
        label: "30 segundos",
        value: 30000,
      },
      {
        label: "1 minuto",
        value: 60000,
      },
      {
        label: "2 minutos",
        value: 120000,
      },
      {
        label: "3 minutos",
        value: 180000,
      },
      {
        label: "5 minutos",
        value: 300000,
      },
    ],
  }),
  methods: {
    async selectFile() {
      this.fileHandler = await window.showSaveFilePicker({
        suggestedName: `Ingressos - ${this.party.name}.${this.exportFormat}`,
        types: [
          {
            description: `Arquivo ${this.exportFormat.toUpperCase()}`,
            accept:
              this.exportFormat === "xlsx"
                ? {
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                      [".xlsx"],
                  }
                : {
                    "text/csv": [".csv"],
                  },
          },
        ],
      });
    },
    async exportProcess() {
      try {
        this.loading = true;
        this.error = false;
        this.succeeded = false;
        this.totalTickets = 0;
        this.processedTickets = new Map();
        this.initExcel();
        this.process();
      } catch (error) {
        this.loading = false;
        this.error = error.message || "Erro ao processar ingressos";
        console.error(error);
      }
    },
    stop() {
      this.loading = false;
      this.succeeded = true;
      this.timeToSync = 0;
      clearTimeout(this.timeout);
    },
    tick() {
      if (this.timeToSync <= 0) return this.process();
      this.timeToSync -= 100;
      this.timeout = setTimeout(this.tick, 100);
    },

    async process() {
      try {
        clearTimeout(this.timeout);
        this.timeout = null;
        if (!this.loading) return;
        let canContinue = true;
        let skip = 0;

        // loadtickets
        while (canContinue) {
          var { tickets, total } = await FINANCIAL.getTickets({
            partyId: this.party.id,
            includeCode: true,
            take: 2000,
            skip,
          });
          if (total) this.totalTickets = total;
          if (tickets?.length === 0) canContinue = false;

          this.appendToExcel(tickets);

          skip += tickets.length;
        }
        await this.exportExcel();
        this.succeeded = true;

        this.timeToSync = this.interval;
        this.tick();
      } catch (error) {
        this.loading = false;
        this.error = error.message || "Erro ao processar ingressos";
        console.error(error);
      }
    },

    initExcel() {
      this.workbook = new ExcelJS.Workbook();
      this.workbook.creator = "TiqOne";
      this.workbook.created = new Date();
      this.worksheet = this.workbook.addWorksheet("Ingressos");

      this.worksheet.columns = [
        { header: "ID", key: "id", width: 20 },
        { header: "Código", key: "code", width: 20 },
        { header: "Status", key: "status", width: 20 },
        { header: "Lote", key: "ticketBlock", width: 20 },
        { header: "Setor", key: "ticketGroup", width: 20 },
        { header: "Data de atualização", key: "updatedAt", width: 20 },
        { header: "Data de criação", key: "createdAt", width: 20 },
      ];
      // this.worksheet.getColum("updatedAt").numFmt = "dd/mm/yyyy hh:mm";
      // this.worksheet.getColum("createdAt").numFmt = "dd/mm/yyyy hh:mm";
    },
    appendToExcel(tickets) {
      tickets.forEach((ticket) => {
        const row = {
          id: ticket.id,
          code: ticket.code,
          status: ticket.Payment.status,
          ticketBlock: ticket.TicketBlock.name,
          ticketGroup: ticket.TicketBlock.TicketGroup.name,
          updatedAt: ticket.updatedAt,
          createdAt: ticket.createdAt,
        };
        if (this.processedTickets.has(ticket.id)) {
          const index = this.processedTickets.get(ticket.id);
          this.worksheet.getRow(index).values = row;
        } else {
          this.worksheet.addRow(row);
          this.processedTickets.set(ticket.id, this.worksheet.rowCount);
        }
      });
    },

    async exportExcel() {
      this.workbook.modified = new Date();
      const buffer = await this.workbook[this.exportFormat].writeBuffer();
      const writable = await this.fileHandler.createWritable();
      await writable.write(buffer);
      await writable.close();
    },
    mathRound(value) {
      return Math.round(value);
    },
  },
  watch: {
    loading: {
      handler(val) {
        this.$emit("loading", val);
      },
    },
    party: {
      handler(val) {
        this.fileHandler = null;
        this.succeeded = false;
      },
    },
    exportFormat: {
      handler(val) {
        this.fileHandler = null;
        this.succeeded = false;
      },
    },
  },
  computed: {
    // Verifica se o evento é nos proximos 3 dias
    isEventInNext3Days() {
      return moment(this.party?.date).isBefore(moment().add(3, "days"));
    },
  },

  mounted() {
    this.isCompatible = "showOpenFilePicker" in self;
  },
  beforeDestroy() {
    this.$emit("loading", false);
  },
};
</script>
